import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  CurrencyYenIcon,
  StarIcon,
  ClockIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

// API base is already configured in apiWithAuth

const WorkHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [workHistory, setWorkHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, ongoing

  useEffect(() => {
    fetchWorkHistory();
    const onCredit = () => fetchWorkHistory();
    if (typeof window !== 'undefined') {
      window.addEventListener('wallet:credit', onCredit);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('wallet:credit', onCredit);
      }
    };
  }, []);

  const fetchWorkHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      // Try multiple endpoints and response shapes
      const candidates = [
        '/work-history',
        '/user/work-history',
        '/users/work-history',
        '/jobs/history',
        '/applications/history',
        '/work/history',
      ];
      let list = [];
      for (const path of candidates) {
        try {
          const res = await client.get(path);
          const d = res?.data;
          const arr = Array.isArray(d) ? d : (Array.isArray(d?.results) ? d.results : (Array.isArray(d?.data) ? d.data : null));
          if (arr) { list = arr; break; }
        } catch (_) { /* try next */ }
      }

      // Fallback: build from wallet transactions (reward-like credit entries)
      if (!Array.isArray(list) || list.length === 0) {
        try {
          const txRes = await client.get('/wallet/transactions');
          const td = txRes?.data;
          const txs = Array.isArray(td) ? td : (Array.isArray(td?.results) ? td.results : (Array.isArray(td?.data) ? td.data : []));
          const rewards = txs.filter(t => {
            const typ = t.transaction_type || t.type || '';
            const st = (t.status || '').toLowerCase();
            const isCredit = ['reward', 'employer_payment', 'credit'].includes(String(typ).toLowerCase());
            return isCredit && (st === 'completed' || st === 'paid' || !st);
          });
          list = rewards.map((t, idx) => ({
            id: t.id || `tx-${idx}`,
            job_title: t.job_title || t.memo || 'お仕事',
            employer_name: t.employer_name || t.merchant || t?.meta?.employer_id || '事業者',
            work_date: t.work_date || t.created_at || t.date,
            work_hours: (t.hours && t.hourly_rate) ? `${Math.max(1, Math.round(t.hours))}h` : '',
            duration_hours: Number(t.hours) || 0,
            hourly_rate: Number(t.hourly_rate) || 0,
            total_payment: Math.max(0, Number(t.net_amount ?? t.amount ?? 0)),
            status: 'completed',
            rating: t.rating,
            location: t.location || ''
          }));
        } catch (_) { /* ignore */ }
      }

      // helper: compute duration from time range like "09:00-13:00"
      const parseHours = (range) => {
        try {
          const [s, e] = String(range || '').split('-');
          const [sh, sm] = s.split(':').map(Number); const [eh, em] = e.split(':').map(Number);
          const val = (eh + (em || 0) / 60) - (sh + (sm || 0) / 60);
          return Number.isFinite(val) && val >= 0 ? val : 0;
        } catch { return 0; }
      };

      const normalized = (Array.isArray(list) ? list : []).map((w, idx) => ({
        id: w.id || String(idx),
        job_title: w.job_title || w.title || 'お仕事',
        employer_name: w.employer_name || '事業者',
        work_date: w.work_date || w.date || new Date().toISOString().slice(0, 10),
        work_hours: w.work_hours || w.hours || '',
        duration_hours: (w.duration_hours ?? w.hours_worked ?? 0) || parseHours(w.work_hours || w.hours),
        hourly_rate: w.hourly_rate ?? 0,
        total_payment: w.total_payment ?? w.net_amount ?? 0,
        status: w.status || 'completed',
        rating: w.rating,
        location: w.location || ''
      }));
      setWorkHistory(normalized);
      console.error('Failed to fetch work history:', error);
      toast.error('勤務履歴の取得に失敗しました。');
      setWorkHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const filteredHistory = workHistory.filter(item => {
    if (filter === 'all') return true;
    return item.status === filter;
  });

  const totalEarnings = workHistory
    .filter(item => item.status === 'completed')
    .reduce((sum, item) => sum + item.total_payment, 0);

  const totalHours = workHistory
    .filter(item => item.status === 'completed')
    .reduce((sum, item) => sum + item.duration_hours, 0);

  const rated = workHistory.filter(item => typeof item.rating === 'number');
  const averageRating = rated.length
    ? rated.reduce((sum, item) => sum + item.rating, 0) / rated.length
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">これまでの仕事</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 統計情報 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-lg">
                <CurrencyYenIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">総獲得報酬</p>
                <p className="text-2xl font-bold text-gray-900">{totalEarnings.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-lg">
                <ClockIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">総勤務時間</p>
                <p className="text-2xl font-bold text-gray-900">{totalHours}時間</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <StarIcon className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">平均評価</p>
                <p className="text-2xl font-bold text-gray-900">{averageRating.toFixed(1)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* フィルター */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex space-x-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium ${filter === 'all'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              すべて ({workHistory.length})
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-lg font-medium ${filter === 'completed'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              完了 ({workHistory.filter(item => item.status === 'completed').length})
            </button>
          </div>
        </div>

        {/* 勤務履歴リスト */}
        <div className="space-y-4">
          {filteredHistory.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <ClipboardDocumentListIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">勤務履歴がありません</h3>
              <p className="text-gray-600">まだ勤務を完了していません。</p>
            </div>
          ) : (
            filteredHistory.map((work) => (
              <WorkHistoryCard key={work.id} work={work} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const WorkHistoryCard = ({ work }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">完了</span>;
      case 'ongoing':
        return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">進行中</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">不明</span>;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{work.job_title}</h3>
          <p className="text-gray-600 mb-2">{work.employer_name}</p>

          <div className="flex items-center text-sm text-gray-500 space-x-4">
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-1" />
              {formatDate(work.work_date)}
            </div>
            <div className="flex items-center">
              <ClockIcon className="w-4 h-4 mr-1" />
              {work.work_hours} ({work.duration_hours}時間)
            </div>
            <div className="flex items-center">
              <MapPinIcon className="w-4 h-4 mr-1" />
              {work.location}
            </div>
          </div>
        </div>

        <div className="text-right">
          {getStatusBadge(work.status)}
        </div>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <div className="flex items-center space-x-6">
          <div className="flex items-center">
            <CurrencyYenIcon className="w-5 h-5 text-green-600 mr-1" />
            <span className="font-semibold text-green-600">{work.total_payment.toLocaleString()}</span>
            <span className="text-sm text-gray-500 ml-1">({work.hourly_rate}/時)</span>
          </div>

          {work.rating && (
            <div className="flex items-center">
              <StarIcon className="w-5 h-5 text-yellow-400 mr-1" />
              <span className="font-medium text-gray-900">{work.rating}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkHistory;